// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * AccentuationAnalysisResponse
 *
 * Response schema for accentuation analysis.
 */
export const zAccentuationAnalysisResponse = z.object({
    analysis_id: z.int(),
    top_accentuations: z.record(z.string(), z.number()),
    confidence: z.number()
});

/**
 * AccessDeniedError
 *
 * 403 body when age verification forbids the action.
 */
export const zAccessDeniedError = z.object({
    detail: z.string(),
    code: z.enum(['age_required', 'age_mismatch']),
    permission: z.enum([
        'card_create',
        'qotd_answer',
        'stream_post',
        'social_match_accept',
        'easy_match_chat',
        'chat_found_friends'
    ])
});

/**
 * AdminLoginRequest
 *
 * Admin login request model.
 */
export const zAdminLoginRequest = z.object({
    username: z.string(),
    password: z.string()
});

/**
 * AdminLoginResponse
 *
 * Admin login response model.
 */
export const zAdminLoginResponse = z.object({
    success: z.boolean(),
    token: z.string().nullish(),
    message: z.string().nullish()
});

/**
 * AdminOut
 *
 * Admin output model.
 */
export const zAdminOut = z.object({
    id: z.int(),
    username: z.string(),
    role: z.string(),
    is_active: z.boolean(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * AdminRole
 *
 * Admin roles.
 */
export const zAdminRole = z.enum([
    'admin',
    'moderator',
    'support',
    'analyst'
]);

/**
 * AdminCreate
 *
 * Admin creation model.
 */
export const zAdminCreate = z.object({
    username: z.string(),
    password: z.string(),
    role: zAdminRole.optional().default('moderator'),
    is_active: z.boolean().optional().default(true)
});

/**
 * AdminUpdate
 *
 * Admin update model.
 */
export const zAdminUpdate = z.object({
    password: z.string().nullish(),
    role: zAdminRole.nullish(),
    is_active: z.boolean().nullish()
});

/**
 * AgeVerificationOut
 */
export const zAgeVerificationOut = z.object({
    status: z.enum([
        'required',
        'verified',
        'grace',
        'mismatch'
    ])
});

/**
 * AndroidInstallReferrerRequest
 *
 * Android client sends Play Install Referrer before registration.
 */
export const zAndroidInstallReferrerRequest = z.object({
    device_id: z.string().min(1).max(255),
    install_referrer: z.string().max(8192).optional().default(''),
    platform: z.literal('android').optional().default('android')
});

/**
 * AppUpdateCheckOut
 *
 * Whether the native client should show the store update screen.
 */
export const zAppUpdateCheckOut = z.object({
    update_screen: z.boolean()
});

/**
 * AppleAppAccountTokenResponse
 *
 * Stable UUID for StoreKit 2 appAccountToken.
 */
export const zAppleAppAccountTokenResponse = z.object({
    app_account_token: z.string()
});

/**
 * AppleLoginRequest
 *
 * Body for POST /auth/apple/login.
 */
export const zAppleLoginRequest = z.object({
    identity_token: z.string(),
    authorization_code: z.string(),
    nonce: z.string(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    device_id: z.string().min(8).max(255).nullish(),
    tg_user_id: z.string().max(50).nullish()
});

/**
 * AppleNotificationRequest
 *
 * Apple ASN V2 webhook body.
 */
export const zAppleNotificationRequest = z.object({
    signedPayload: z.string()
});

/**
 * AspectBlock
 */
export const zAspectBlock = z.object({
    slug: z.enum([
        'perceiving',
        'using',
        'understanding',
        'managing'
    ]),
    title: z.string(),
    score: z.int()
});

/**
 * AvatarInfo
 *
 * Avatar information schema.
 */
export const zAvatarInfo = z.object({
    id: z.string(),
    url: z.string().min(1)
});

/**
 * AvatarUrls
 *
 * Avatar URLs for small, medium, and large sizes (always non-null URIs).
 */
export const zAvatarUrls = z.object({
    small: z.url().min(1),
    medium: z.url().min(1),
    large: z.url().min(1)
});

/**
 * AvatarsListResponse
 *
 * Avatars list response schema.
 */
export const zAvatarsListResponse = z.object({
    stock: z.array(zAvatarInfo)
});

/**
 * BlockUserResponse
 *
 * Block user response schema.
 */
export const zBlockUserResponse = z.object({
    status: z.string(),
    message: z.string()
});

/**
 * CardCreate
 *
 * Body for creating a card.
 */
export const zCardCreate = z.object({
    question: z.string().min(1).max(35),
    answer: z.string().min(1).max(100),
    order_index: z.int().gte(0).optional().default(0)
});

/**
 * CardLikerOut
 *
 * User who liked a card (for own profile reactions).
 */
export const zCardLikerOut = z.object({
    user_id: z.int(),
    name: z.string(),
    avatar: zAvatarUrls,
    avatar_emoji: z.string().nullable()
});

/**
 * CardUpdate
 *
 * Body for updating a card. Send only fields to change.
 */
export const zCardUpdate = z.object({
    question: z.string().min(1).max(35).nullish(),
    answer: z.string().min(1).max(100).nullish(),
    order_index: z.int().gte(0).nullish()
});

/**
 * CharacterLastMessageResponse
 *
 * Preview line for home-screen persona card.
 */
export const zCharacterLastMessageResponse = z.object({
    last_message: z.string().min(1).nullable()
});

/**
 * CharacterSendMessageRequest
 *
 * Body for synchronous AI message.
 */
export const zCharacterSendMessageRequest = z.object({
    user_id: z.int(),
    message: z.string()
});

/**
 * CharacterSendMessageResponse
 *
 * Synchronous AI reply (HTTP status conveys success; no duplicate status field).
 */
export const zCharacterSendMessageResponse = z.object({
    user_message: z.string(),
    character_response: z.string()
});

/**
 * CharacterSlug
 */
export const zCharacterSlug = z.enum([
    'luni',
    'sola',
    'mars'
]);

/**
 * CharacterStreamRequest
 *
 * Streaming chat body (alias ``prompt`` for user text, same as legacy stream_chat).
 */
export const zCharacterStreamRequest = z.object({
    user_id: z.int(),
    prompt: z.string()
});

/**
 * ComparisonAverage
 */
export const zComparisonAverage = z.object({
    score: z.number()
});

/**
 * ComparisonBucket
 */
export const zComparisonBucket = z.object({
    score: z.number(),
    count: z.int()
});

/**
 * ComparisonCard
 */
export const zComparisonCard = z.object({
    slug: z.enum([
        'total',
        'perceiving',
        'using',
        'understanding',
        'managing'
    ]),
    buckets: z.array(zComparisonBucket),
    average: zComparisonAverage.nullable()
});

/**
 * DeleteUserRequest
 *
 * Optional body for account deletion.
 */
export const zDeleteUserRequest = z.object({
    reason: z.string().max(4000).nullish()
});

/**
 * DeviceTokenDeleteRequest
 *
 * Request to unlink push token(s) for a device.
 */
export const zDeviceTokenDeleteRequest = z.object({
    device_id: z.string().min(8).max(255)
});

/**
 * DeviceTokenUpsertRequest
 *
 * Request to register/update device push token.
 */
export const zDeviceTokenUpsertRequest = z.object({
    device_id: z.string().min(8).max(255),
    fcm_token: z.string().min(32).max(512)
});

/**
 * DotPlotActionMeta
 *
 * One action in the catalog.
 */
export const zDotPlotActionMeta = z.object({
    key: z.string(),
    bit: z.int(),
    label: z.string()
});

/**
 * DotPlotMessages
 *
 * Sticky Messages column. Omit a persona/friends key when its count is 0.
 */
export const zDotPlotMessages = z.object({
    friends: z.int().nullish(),
    luni: z.int().nullish(),
    sola: z.int().nullish(),
    mars: z.int().nullish()
});

/**
 * DotPlotMeta
 *
 * Window + catalog for a chunk or /meta.
 */
export const zDotPlotMeta = z.object({
    range_start: z.iso.date(),
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish(),
    viewport_start: z.iso.date(),
    built_at: z.iso.datetime({ offset: true }),
    timezone: z.string().optional().default('UTC'),
    actions: z.array(zDotPlotActionMeta),
    next_cursor: z.int().nullish(),
    has_more: z.boolean().nullish(),
    total_users: z.int().nullish(),
    job_version: z.int().nullish()
});

/**
 * DotPlotPortrait
 *
 * Sticky User column. Omit age/gender/city/app_language/status when unset.
 */
export const zDotPlotPortrait = z.object({
    age: z.int().nullish(),
    gender: z.string().nullish(),
    city: z.string().nullish(),
    app_language: z.string().nullish(),
    matching_languages: z.array(z.string()).optional(),
    friends: z.int().optional().default(0),
    status: z.string().nullish()
});

/**
 * DotPlotUserRow
 *
 * One user row in the matrix.
 */
export const zDotPlotUserRow = z.object({
    user_id: z.int(),
    platforms: z.array(z.string()),
    registered_at: z.iso.datetime({ offset: true }).nullable(),
    portrait: zDotPlotPortrait,
    messages: zDotPlotMessages,
    days: z.record(z.string(), z.array(z.string()))
});

/**
 * DotPlotChunkResponse
 *
 * Paginated matrix payload.
 */
export const zDotPlotChunkResponse = z.object({
    meta: zDotPlotMeta,
    users: z.array(zDotPlotUserRow)
});

/**
 * EiAnswerOption
 */
export const zEiAnswerOption = z.object({
    id: z.string(),
    text: z.string()
});

/**
 * EiArchetypeBlock
 */
export const zEiArchetypeBlock = z.object({
    id: z.enum([
        'observer',
        'catalyst',
        'analyst',
        'balancer'
    ])
});

/**
 * EiMainAspects
 */
export const zEiMainAspects = z.object({
    perceiving: zAspectBlock,
    using: zAspectBlock,
    understanding: zAspectBlock,
    managing: zAspectBlock
});

/**
 * EiProfileNotReady
 */
export const zEiProfileNotReady = z.object({
    status: z.enum(['not_started', 'in_progress']),
    metrics: z.null(),
    top_percent: z.null(),
    archetype: z.null(),
    visible_to_others: z.null()
});

/**
 * EiQuestionItem
 */
export const zEiQuestionItem = z.object({
    id: z.string(),
    text: z.string(),
    answers: z.array(zEiAnswerOption)
});

/**
 * EiSaveAnswersRequest
 */
export const zEiSaveAnswersRequest = z.object({
    group_id: z.string(),
    current_index: z.int().gte(0),
    answers: z.record(z.string(), z.string())
});

/**
 * EiSaveAnswersResponse
 *
 * Progress after saving one screen — no question catalog.
 */
export const zEiSaveAnswersResponse = z.object({
    status: z.enum(['in_progress', 'completed']),
    current_index: z.int(),
    total_screens: z.int()
});

/**
 * EiScreen
 */
export const zEiScreen = z.object({
    id: z.string(),
    kind: z.enum([
        'text_sliders',
        'image_sliders',
        'text_single_choice',
        'image_single_choice'
    ]),
    text: z.string(),
    image_url: z.string().nullable(),
    questions: z.array(zEiQuestionItem),
    required_question_ids: z.array(z.string())
});

/**
 * EiSessionResponse
 */
export const zEiSessionResponse = z.object({
    status: z.enum(['in_progress', 'completed']),
    current_index: z.int(),
    answers: z.record(z.string(), z.record(z.string(), z.string())),
    questions: z.array(zEiScreen),
    total_screens: z.int()
});

/**
 * EiStartRequest
 */
export const zEiStartRequest = z.object({
    restart: z.boolean().optional().default(false)
});

/**
 * EiVisibilityPatch
 */
export const zEiVisibilityPatch = z.object({
    visible_to_others: z.boolean()
});

/**
 * ErrorResponse
 *
 * Standard error body.
 */
export const zErrorResponse = z.object({
    detail: z.string()
});

/**
 * FeedCardAuthorOut
 *
 * Anonymous author line for feed card (city + avatar; label is client-side).
 */
export const zFeedCardAuthorOut = z.object({
    city: z.string().min(1),
    avatar: zAvatarUrls
});

/**
 * FeedCardOut
 *
 * Single card in public feed.
 */
export const zFeedCardOut = z.object({
    id: z.int(),
    question: z.string().min(1),
    answer: z.string().min(1),
    created_at: z.iso.datetime({ offset: true }),
    author: zFeedCardAuthorOut,
    count_stars: z.int().gte(0),
    current_user_starred: z.boolean()
});

/**
 * FeedStatusResponse
 *
 * Blue-dot indicator for tab bar.
 */
export const zFeedStatusResponse = z.object({
    has_new: z.boolean()
});

/**
 * ForeignCardOut
 *
 * Card on another user's profile.
 */
export const zForeignCardOut = z.object({
    is_own: z.literal(false),
    id: z.int(),
    question: z.string().min(1),
    answer: z.string().min(1),
    count_likes: z.int().gte(0),
    count_stars: z.int().gte(0),
    current_user_liked: z.boolean()
});

/**
 * FriendshipAnalytics
 *
 * Friendship analytics schema.
 */
export const zFriendshipAnalytics = z.object({
    total_friendships: z.int(),
    pending_requests: z.int(),
    accepted_requests: z.int(),
    rejected_requests: z.int()
});

/**
 * GoogleLoginRequest
 *
 * Body for POST /auth/google/login.
 */
export const zGoogleLoginRequest = z.object({
    id_token: z.string(),
    device_id: z.string().min(8).max(255).nullish()
});

/**
 * InviteContextResponse
 *
 * Контекст приглашения по tg_ref_code.
 */
export const zInviteContextResponse = z.object({
    type: z.string(),
    inviter_id: z.int().nullish()
});

/**
 * LeaderboardEntry
 */
export const zLeaderboardEntry = z.object({
    user_id: z.int(),
    rank: z.int(),
    luni_name: z.string().min(1),
    luni_lastname: z.string().nullable(),
    avatar_urls: zAvatarUrls,
    total: z.number(),
    is_highlighted: z.boolean()
});

/**
 * EiLeaderboardResponse
 */
export const zEiLeaderboardResponse = z.object({
    entries: z.array(zLeaderboardEntry),
    viewer: zLeaderboardEntry.nullable()
});

/**
 * LogoutRequest
 *
 * End the session belonging to the given refresh token.
 */
export const zLogoutRequest = z.object({
    refresh_token: z.string().min(1),
    device_id: z.string().min(8).max(255).nullish()
});

/**
 * LuniAnalytics
 *
 * Luni chat analytics schema.
 */
export const zLuniAnalytics = z.object({
    total_dialogues: z.int(),
    dialogues_today: z.int(),
    dialogues_this_week: z.int(),
    dialogues_this_month: z.int(),
    average_messages_per_dialogue: z.number()
});

/**
 * LuniLastMessageResponse
 *
 * Response schema for GET luni_get_last_message.
 */
export const zLuniLastMessageResponse = z.object({
    last_message: z.string()
});

/**
 * LuniMessageRequest
 *
 * Request schema for Luni message exchange.
 */
export const zLuniMessageRequest = z.object({
    message: z.string(),
    user_id: z.int()
});

/**
 * LuniMessageResponse
 *
 * Response schema for Luni message exchange.
 */
export const zLuniMessageResponse = z.object({
    status: z.string(),
    user_message: z.string(),
    luni_response: z.string()
});

/**
 * MessageDelivery
 *
 * Message delivery model.
 */
export const zMessageDelivery = z.object({
    from_user_id: z.int(),
    to_user_id: z.int()
});

/**
 * OpenAIStreamChatRequest
 *
 * Streaming chat request for useCompletion.
 */
export const zOpenAiStreamChatRequest = z.object({
    prompt: z.string(),
    user_id: z.int()
});

/**
 * OwnCardOut
 *
 * Card on the owner's profile.
 */
export const zOwnCardOut = z.object({
    is_own: z.literal(true),
    id: z.int(),
    question: z.string().min(1),
    answer: z.string().min(1),
    count_likes: z.int().gte(0),
    count_stars: z.int().gte(0),
    likes: z.array(zCardLikerOut)
});

export const zCardOut = z.discriminatedUnion('is_own', [
    zOwnCardOut,
    zForeignCardOut
]);

/**
 * CardsResponse
 *
 * Response for GET /user/{user_id}/cards.
 */
export const zCardsResponse = z.object({
    cards: z.array(zCardOut)
});

/**
 * OwnEmailError
 *
 * 403 when the confirmer typed the requester's own email.
 */
export const zOwnEmailError = z.object({
    detail: z.string(),
    code: z.literal('own_email')
});

/**
 * PendingRequestsAnalytics
 *
 * Analytics schema for pending friendship requests.
 */
export const zPendingRequestsAnalytics = z.object({
    total_pending: z.int(),
    by_age_group: z.record(z.string(), z.int()),
    by_gender: z.record(z.string(), z.int()),
    by_gender_and_age: z.record(z.string(), z.record(z.string(), z.int()))
});

/**
 * PermissionAllowed
 *
 * Action is allowed. No reason field — the client must not see undefined/null.
 */
export const zPermissionAllowed = z.object({
    allowed: z.literal(true)
});

/**
 * PermissionDenied
 *
 * Action is closed by age status.
 */
export const zPermissionDenied = z.object({
    allowed: z.literal(false),
    reason: z.enum(['age_required', 'age_mismatch'])
});

/**
 * PersonalityArchetypeBlock
 *
 * The app shows the name and the text of the archetype by `id`.
 */
export const zPersonalityArchetypeBlock = z.object({
    id: z.enum([
        'seeker',
        'creator',
        'realist',
        'jester',
        'aesthete',
        'support',
        'thinker',
        'rebel',
        'achiever',
        'leader',
        'dreamer',
        'visionary'
    ])
});

/**
 * PersonalityBigFiveBlock
 *
 * Each scale 0..100; the app shows the poles.
 */
export const zPersonalityBigFiveBlock = z.object({
    description: z.string().min(1),
    openness: z.int().gte(0).lte(100),
    conscientiousness: z.int().gte(0).lte(100),
    extraversion: z.int().gte(0).lte(100),
    agreeableness: z.int().gte(0).lte(100),
    emotional_stability: z.int().gte(0).lte(100)
});

/**
 * PersonalityFriendshipBlock
 */
export const zPersonalityFriendshipBlock = z.object({
    description: z.string().min(1),
    time: z.int().gte(0).lte(100),
    words: z.int().gte(0).lte(100),
    help: z.int().gte(0).lte(100),
    presence: z.int().gte(0).lte(100),
    attention: z.int().gte(0).lte(100)
});

/**
 * PersonalityHumorBlock
 *
 * A point on the humor plane; the app shows the style of its 45° sector.
 */
export const zPersonalityHumorBlock = z.object({
    x_axis: z.number().gte(-100).lte(100),
    y_axis: z.number().gte(-100).lte(100),
    description: z.string()
});

/**
 * PersonalityImageUploadHeaders
 *
 * Required headers the client must send with the presigned PUT.
 */
export const zPersonalityImageUploadHeaders = z.object({
    'Content-Type': z.string()
});

/**
 * PersonalityImageUploadRequest
 *
 * Request payload for generating a presigned upload URL.
 */
export const zPersonalityImageUploadRequest = z.object({
    filename: z.string().min(1),
    content_type: z.string().min(1).regex(/^image\/.+$/)
});

/**
 * PersonalityImageUploadResponse
 *
 * Presigned upload data returned to the client.
 */
export const zPersonalityImageUploadResponse = z.object({
    upload_url: z.string(),
    public_url: z.string(),
    method: z.literal('PUT'),
    headers: zPersonalityImageUploadHeaders
});

/**
 * PersonalityPayloadPartial
 *
 * Blocks returned in API; null means hidden (friend view) or absent.
 */
export const zPersonalityPayloadPartial = z.object({
    archetype: zPersonalityArchetypeBlock.nullable(),
    big_five: zPersonalityBigFiveBlock.nullable(),
    friendship: zPersonalityFriendshipBlock.nullable(),
    humor: zPersonalityHumorBlock.nullable()
});

/**
 * PersonalityVisibilityMap
 *
 * What the owner allows friends to see.
 */
export const zPersonalityVisibilityMap = z.object({
    archetype: z.boolean(),
    big_five: z.boolean(),
    friendship: z.boolean(),
    humor: z.boolean()
});

/**
 * PersonalityProfileBlock
 *
 * Single psychoprofile snapshot for API.
 */
export const zPersonalityProfileBlock = z.object({
    personality: zPersonalityPayloadPartial,
    visibility: zPersonalityVisibilityMap
});

/**
 * PersonalityProfileResponse
 */
export const zPersonalityProfileResponse = z.object({
    personality_profile: zPersonalityProfileBlock.nullable()
});

/**
 * PersonalityVisibilityPatch
 *
 * PATCH body: only send keys to change; omitted keys stay unchanged.
 */
export const zPersonalityVisibilityPatch = z.object({
    archetype: z.boolean().nullish(),
    big_five: z.boolean().nullish(),
    friendship: z.boolean().nullish(),
    humor: z.boolean().nullish()
});

/**
 * PhotoLivenessMetric
 *
 * Liveness (anti-spoof) result nested in quality metrics.
 */
export const zPhotoLivenessMetric = z.object({
    value: z.string().nullable(),
    confidence: z.number().nullable()
});

/**
 * PhotoQualityCheck
 *
 * Модель для проверки качества фотографии.
 */
export const zPhotoQualityCheck = z.object({
    image: z.string()
});

/**
 * PhotoQualityMetrics
 *
 * Vendor quality scores plus liveness. Extra keys from the provider are kept.
 */
export const zPhotoQualityMetrics = z.object({
    total_score: z.number().nullish(),
    sharpness_score: z.number().nullish(),
    illumination_score: z.number().nullish(),
    neutral_emotion_score: z.number().nullish(),
    not_masked_score: z.number().nullish(),
    watermark_score: z.number().nullish(),
    margin_inner_deviation: z.number().nullish(),
    liveness: zPhotoLivenessMetric.nullish()
});

/**
 * PhotoQualityResponse
 *
 * Модель ответа для проверки качества фотографии.
 */
export const zPhotoQualityResponse = z.object({
    success: z.boolean(),
    total_score: z.number().nullable(),
    quality_level: z.string().nullable(),
    quality_metrics: zPhotoQualityMetrics.nullable(),
    recommendations: z.array(z.string()).nullable()
});

/**
 * PushTrackingDayCell
 *
 * Raw send/open counts. Frontend computes open %.
 */
export const zPushTrackingDayCell = z.object({
    sent: z.int(),
    opened: z.int()
});

/**
 * PushTrackingNotificationMeta
 *
 * One grouped push template in the catalog.
 */
export const zPushTrackingNotificationMeta = z.object({
    key: z.string(),
    bit: z.int(),
    label: z.string()
});

/**
 * PushTrackingMeta
 *
 * Window + catalog for the matrix or /meta.
 */
export const zPushTrackingMeta = z.object({
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish(),
    built_at: z.iso.datetime({ offset: true }),
    timezone: z.string().optional().default('UTC'),
    notifications: z.array(zPushTrackingNotificationMeta),
    job_version: z.int().nullish()
});

/**
 * PushTrackingRow
 *
 * One template row; day keys omitted when sent=0.
 */
export const zPushTrackingRow = z.object({
    key: z.string(),
    days: z.record(z.string(), zPushTrackingDayCell)
});

/**
 * PushTrackingMatrixResponse
 *
 * Full matrix payload (no row pagination).
 */
export const zPushTrackingMatrixResponse = z.object({
    meta: zPushTrackingMeta,
    rows: z.array(zPushTrackingRow)
});

/**
 * QotdAnswerRequest
 */
export const zQotdAnswerRequest = z.object({
    answer: z.string().min(1).max(100)
});

/**
 * QotdQuestionOut
 *
 * Today's Question of the Day for the viewer.
 */
export const zQotdQuestionOut = z.object({
    id: z.int(),
    question: z.string().min(1)
});

/**
 * QotdCurrentResponse
 *
 * Current QOTD state for blur screen.
 */
export const zQotdCurrentResponse = z.object({
    current_question: zQotdQuestionOut.nullish(),
    locked: z.boolean().optional().default(true),
    user_has_answered: z.boolean().optional().default(false)
});

/**
 * RankStatsResponse
 *
 * Synthetic stats for top three ranks.
 */
export const zRankStatsResponse = z.object({
    legend: z.int().gte(0),
    planet: z.int().gte(0),
    monster: z.int().gte(0)
});

/**
 * RankTier
 */
export const zRankTier = z.enum([
    'caterpillar',
    'yogurt',
    'turbo',
    'samurai',
    'legend',
    'planet',
    'monster'
]);

/**
 * RevokeAllSessionsResponse
 *
 * Response after revoking all sessions.
 */
export const zRevokeAllSessionsResponse = z.object({
    revoked_count: z.int()
});

/**
 * RuStoreNotificationRequest
 *
 * RuStore payment callback body.
 */
export const zRuStoreNotificationRequest = z.object({
    id: z.string(),
    timestamp: z.string().nullish(),
    payload: z.string()
});

/**
 * SaveHeardAboutRequest
 *
 * Pre-registration free-text answer: how the user learned about Luni.
 */
export const zSaveHeardAboutRequest = z.object({
    text: z.string().min(1).max(1000),
    device_id: z.string().max(255).nullish(),
    tg_user_id: z.string().max(255).nullish()
});

/**
 * SaveInstagramRequest
 *
 * Pre-registration Instagram username (already normalized by client).
 */
export const zSaveInstagramRequest = z.object({
    instagram: z.string().min(1).max(30)
});

/**
 * SaveIntentionRequest
 *
 * Pre-registration onboarding intention.
 */
export const zSaveIntentionRequest = z.object({
    intention: z.string().min(1).max(1000),
    device_id: z.string().max(255).nullish(),
    tg_user_id: z.string().max(255).nullish()
});

/**
 * SaveVpnAnswerRequest
 *
 * Pre-registration answer to the "Need Luni VPN?" onboarding screen.
 */
export const zSaveVpnAnswerRequest = z.object({
    wants_vpn: z.boolean(),
    device_id: z.string().max(255).nullish(),
    tg_user_id: z.string().max(255).nullish()
});

/**
 * ScoreBlock
 */
export const zScoreBlock = z.object({
    score: z.int(),
    level: z.enum([
        'low',
        'below_average',
        'average',
        'above_average',
        'high',
        'very_high'
    ]),
    level_label: z.string(),
    gauge_ratio: z.number()
});

/**
 * EiMainBlock
 */
export const zEiMainBlock = z.object({
    total: zScoreBlock,
    aspects: zEiMainAspects
});

/**
 * EiMetricsBrief
 */
export const zEiMetricsBrief = z.object({
    total: zScoreBlock,
    perceiving: zAspectBlock,
    using: zAspectBlock,
    understanding: zAspectBlock,
    managing: zAspectBlock
});

/**
 * EiProfileCompleted
 */
export const zEiProfileCompleted = z.object({
    status: z.literal('completed'),
    metrics: zEiMetricsBrief,
    top_percent: z.int().nullable(),
    archetype: zEiArchetypeBlock,
    visible_to_others: z.boolean().nullable()
});

export const zEiProfileResponse = z.discriminatedUnion('status', [
    zEiProfileNotReady.extend({ status: z.literal('in_progress') }),
    zEiProfileNotReady.extend({ status: z.literal('not_started') }),
    zEiProfileCompleted
]);

/**
 * SendCodeRequest
 *
 * Request body for POST /auth/email/send-code.
 */
export const zSendCodeRequest = z.object({
    email: z.email()
});

/**
 * SessionOut
 *
 * Active user session.
 */
export const zSessionOut = z.object({
    id: z.int(),
    session_id: z.string(),
    device_id: z.string().nullish(),
    platform: z.string().nullish(),
    ip: z.string().nullish(),
    user_agent: z.string().nullish(),
    created_at: z.iso.datetime({ offset: true }),
    last_used_at: z.iso.datetime({ offset: true }),
    expires_at: z.iso.datetime({ offset: true })
});

/**
 * SessionListResponse
 *
 * List of active sessions.
 */
export const zSessionListResponse = z.object({
    sessions: z.array(zSessionOut)
});

/**
 * SubscaleBlock
 */
export const zSubscaleBlock = z.object({
    slug: z.enum([
        'reading_faces',
        'overall_vibe',
        'understanding_situations',
        'inner_motor',
        'empathy',
        'mood_shifts',
        'complex_emotions',
        'situation_development',
        'self_control',
        'influence'
    ]),
    title: z.string(),
    score: z.number(),
    bar_fill_ratio: z.number()
});

/**
 * SpectrumAspect
 */
export const zSpectrumAspect = z.object({
    slug: z.enum([
        'perceiving',
        'using',
        'understanding',
        'managing'
    ]),
    title: z.string(),
    score: z.number(),
    bar_fill_ratio: z.number(),
    subscales: z.array(zSubscaleBlock)
});

/**
 * EiResultsResponse
 */
export const zEiResultsResponse = z.object({
    main: zEiMainBlock,
    archetype: zEiArchetypeBlock,
    spectrum: z.array(zSpectrumAspect),
    superpowers: z.array(zSubscaleBlock),
    growth: z.array(zSubscaleBlock),
    comparison: z.array(zComparisonCard)
});

/**
 * SubscriptionCancelErrorResponse
 *
 * Error body for cancel when store does not support in-app cancel.
 */
export const zSubscriptionCancelErrorResponse = z.object({
    code: z.literal('cancel_not_supported_for_store'),
    detail: z.string()
});

/**
 * SubscriptionInfo
 *
 * Short subscription info embedded in UserOut.
 */
export const zSubscriptionInfo = z.object({
    active: z.boolean(),
    plan: z.literal('premium').nullable()
});

/**
 * SubscriptionStatusResponse
 *
 * Subscription status response schema.
 */
export const zSubscriptionStatusResponse = z.object({
    active: z.boolean(),
    plan: z.literal('premium').nullable(),
    cancelled: z.boolean(),
    renewal_date: z.iso.datetime({ offset: true }).nullable(),
    expires_at: z.iso.datetime({ offset: true }).nullable(),
    provider: z.enum([
        'cloudpayments',
        'google_play',
        'app_store',
        'rustore',
        'rank_reward',
        'invitation_bonus',
        'manual_grant'
    ]).nullable(),
    tier: z.enum([
        'monster_7d',
        'monster_1m',
        'monster_3m'
    ]).nullable(),
    pending_tier: z.enum([
        'monster_7d',
        'monster_1m',
        'monster_3m'
    ]).nullable(),
    trial_eligible: z.boolean(),
    trial_active: z.boolean(),
    trial_ends_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * SupportMessageRequest
 *
 * Support message request schema.
 */
export const zSupportMessageRequest = z.object({
    user_id: z.int(),
    sender_name: z.string().min(1).max(128),
    message: z.string().min(10).max(2000)
});

/**
 * SupportMessageResponse
 *
 * Support message response schema.
 */
export const zSupportMessageResponse = z.object({
    status: z.string(),
    message: z.string()
});

/**
 * SystemMetrics
 *
 * System metrics schema.
 */
export const zSystemMetrics = z.object({
    redis_connected: z.boolean(),
    redis_memory_usage: z.string(),
    redis_connected_clients: z.int(),
    database_connections: z.int(),
    websocket_connections: z.int()
});

/**
 * TelegramUserCreate
 *
 * Schema for creating/registering a Telegram user (POST /register).
 */
export const zTelegramUserCreate = z.object({
    telegram_id: z.int(),
    first_name: z.string().max(255).nullish(),
    last_name: z.string().max(255).nullish(),
    username: z.string().max(255).nullish(),
    is_forum: z.boolean().nullish(),
    title: z.string().max(255).nullish(),
    type: z.string().max(50).optional().default('private'),
    start_payload: z.string().max(128).nullish()
});

/**
 * TelegramUserOut
 *
 * Schema for Telegram user output.
 */
export const zTelegramUserOut = z.object({
    telegram_id: z.int(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    username: z.string().nullish(),
    is_forum: z.boolean().nullish(),
    title: z.string().nullish(),
    type: z.string().optional().default('private'),
    start_payload: z.string().nullish(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * TelegramUserUpdate
 *
 * Schema for updating a Telegram user.
 */
export const zTelegramUserUpdate = z.object({
    first_name: z.string().max(255).nullish(),
    last_name: z.string().max(255).nullish(),
    username: z.string().max(255).nullish(),
    is_forum: z.boolean().nullish(),
    title: z.string().max(255).nullish(),
    type: z.string().max(50).nullish(),
    start_payload: z.string().max(128).nullish()
});

/**
 * ThoughtCreateRequest
 *
 * Request schema for creating thought.
 */
export const zThoughtCreateRequest = z.object({
    title: z.string().max(100),
    thoughts: z.string(),
    thoughts_date: z.iso.datetime({ offset: true }).nullish()
});

/**
 * ThoughtResponse
 *
 * Response schema for thought (frontend contract).
 */
export const zThoughtResponse = z.object({
    id: z.int(),
    user_id: z.int(),
    title: z.string().nullable(),
    thoughts: z.string().nullable(),
    thoughts_date: z.iso.datetime({ offset: true }),
    is_ai_generated: z.boolean(),
    is_read: z.boolean()
});

/**
 * AIThoughtsResponse
 *
 * Response schema for AI generated thoughts.
 */
export const zAiThoughtsResponse = z.object({
    thoughts: z.array(zThoughtResponse),
    unread_only: z.boolean()
});

/**
 * ThoughtUpdateRequest
 *
 * Request schema for updating thought.
 */
export const zThoughtUpdateRequest = z.object({
    title: z.string().max(100).nullish(),
    thoughts: z.string().nullish()
});

/**
 * ThoughtsByDateResponse
 *
 * Response schema for thoughts by date.
 */
export const zThoughtsByDateResponse = z.object({
    thoughts: z.array(zThoughtResponse),
    date: z.string()
});

/**
 * ThoughtsListResponse
 *
 * Response schema for thoughts list.
 */
export const zThoughtsListResponse = z.object({
    thoughts: z.array(zThoughtResponse),
    total_count: z.int(),
    limit: z.int(),
    offset: z.int()
});

/**
 * TokenPairResponse
 *
 * Access and refresh token pair.
 */
export const zTokenPairResponse = z.object({
    access_token: z.string().min(1),
    refresh_token: z.string().min(1)
});

/**
 * TokenRefreshRequest
 *
 * Refresh token request payload.
 */
export const zTokenRefreshRequest = z.object({
    refresh_token: z.string().min(1)
});

/**
 * TranslationContext
 *
 * Where the texts come from: scopes access (texts of one friend).
 */
export const zTranslationContext = z.object({
    type: z.literal('friend'),
    friend_id: z.int()
});

/**
 * TranslationItemIn
 *
 * One text to translate. `key` is an opaque id that links it to the response item.
 *
 * A card_answer belongs to the card_question right before it in `items`.
 */
export const zTranslationItemIn = z.object({
    key: z.string().min(1).max(100),
    text: z.string().min(1).max(2000),
    kind: z.enum([
        'name',
        'lastname',
        'interests',
        'expertise',
        'card_question',
        'card_answer',
        'personality_description',
        'board_message'
    ])
});

/**
 * TranslateRequest
 */
export const zTranslateRequest = z.object({
    items: z.array(zTranslationItemIn).min(1).max(50),
    target_lang: z.enum([
        'ru',
        'en',
        'es'
    ]),
    context: zTranslationContext
});

/**
 * TranslationItemOut
 */
export const zTranslationItemOut = z.object({
    key: z.string(),
    text: z.string()
});

/**
 * TranslateResponse
 */
export const zTranslateResponse = z.object({
    items: z.array(zTranslationItemOut)
});

/**
 * TrustGateConfirmRequest
 *
 * Подтверждение по ссылке: Luni-ID цели + данные подтверждающего.
 */
export const zTrustGateConfirmRequest = z.object({
    luni_id: z.string().min(1).max(100),
    email: z.email(),
    name: z.string().min(1).max(100),
    app_language: z.string().max(10).nullish()
});

/**
 * TrustGateConfirmResponse
 *
 * Acknowledgement after friend confirms.
 */
export const zTrustGateConfirmResponse = z.object({
    success: z.boolean().optional().default(true),
    message: z.string().optional().default('ok')
});

/**
 * TrustGateStatusResponse
 *
 * Whether the current user's trust-gate has been lifted.
 */
export const zTrustGateStatusResponse = z.object({
    confirmed: z.boolean()
});

/**
 * UpdateUserForbiddenError
 *
 * 403 body when character/subscription gate rejects update_user.
 */
export const zUpdateUserForbiddenError = z.object({
    detail: z.string(),
    code: z.enum(['subscription_required', 'forbidden'])
});

/**
 * UserAgeCheckRequest
 *
 * POST /auth/check_photo body. Both fields required; no extras.
 */
export const zUserAgeCheckRequest = z.object({
    image: z.string(),
    attempt_id: z.string().min(1)
});

/**
 * UserAgeCheckResponseV2
 *
 * New selfie response. Both fields always present; no reason.
 */
export const zUserAgeCheckResponseV2 = z.object({
    result: z.enum([
        'verified',
        'retry',
        'grace_started',
        'parental_consent',
        'blocked',
        'error'
    ]),
    attempts_left: z.int().gte(0)
});

/**
 * UserAnalytics
 *
 * User analytics schema.
 */
export const zUserAnalytics = z.object({
    total_users: z.int(),
    active_users: z.int(),
    online_users: z.int(),
    blocked_users: z.int(),
    by_age_group: z.record(z.string(), z.int()),
    by_gender: z.record(z.string(), z.int())
});

/**
 * UserAnswerOut
 *
 * Viewer's QOTD answer when present.
 */
export const zUserAnswerOut = z.object({
    question_id: z.int(),
    answer: z.string(),
    count_stars: z.int()
});

/**
 * FeedListResponse
 *
 * Paginated feed response.
 */
export const zFeedListResponse = z.object({
    items: z.array(zFeedCardOut),
    next_cursor: z.string().nullable(),
    has_more: z.boolean(),
    locked: z.boolean(),
    current_question: zQotdQuestionOut.nullable(),
    user_answer: zUserAnswerOut.nullable()
});

/**
 * UserBlockRequest
 *
 * Запрос на блокировку пользователя.
 */
export const zUserBlockRequest = z.object({
    reason: z.string().nullish().default('manual')
});

/**
 * UserCheck
 *
 * User check model.
 */
export const zUserCheck = z.object({
    tg_user_id: z.string().nullish(),
    user_id: z.int().nullish(),
    device_id: z.string().nullish()
});

/**
 * UserCheckMissing
 */
export const zUserCheckMissing = z.object({
    exists: z.literal(false),
    user: z.null(),
    access_token: z.null(),
    refresh_token: z.null()
});

/**
 * UserCheckUpdateMissing
 */
export const zUserCheckUpdateMissing = z.object({
    exists: z.literal(false),
    user: z.null(),
    access_token: z.null(),
    refresh_token: z.null(),
    update_screen: z.boolean().optional().default(false)
});

/**
 * UserCreate
 *
 * User creation model.
 */
export const zUserCreate = z.object({
    luni_name: z.string().min(1).max(50),
    luni_lastname: z.string().nullish(),
    email: z.string().nullish(),
    tg_user_id: z.string().max(50).nullish(),
    gender: z.union([z.literal(1), z.literal(2)]),
    age: z.int().nullish(),
    date_of_birth: z.iso.date().nullish(),
    country_ISO_code: z.string().nullish(),
    app_language: z.enum([
        'ru',
        'en',
        'es'
    ]).nullish(),
    timezone: z.string().nullish(),
    city: z.string().max(50).nullish(),
    interests: z.string().nullish(),
    avatar_urls: zAvatarUrls.nullish(),
    tg_username: z.string().max(50).nullish(),
    is_invisible: z.boolean().nullish(),
    device_id: z.string().nullish(),
    tg_ref_code: z.string().nullish(),
    blocked: z.boolean().nullish(),
    reason_blocked: z.string().nullish(),
    apple_identity_token: z.string().nullish(),
    apple_nonce: z.string().nullish(),
    google_id_token: z.string().nullish()
});

/**
 * UserPermissions
 */
export const zUserPermissions = z.object({
    card_create: z.discriminatedUnion('allowed', [
        zPermissionAllowed,
        zPermissionDenied
    ]),
    qotd_answer: z.discriminatedUnion('allowed', [
        zPermissionAllowed,
        zPermissionDenied
    ]),
    stream_post: z.discriminatedUnion('allowed', [
        zPermissionAllowed,
        zPermissionDenied
    ]),
    social_match_accept: z.discriminatedUnion('allowed', [
        zPermissionAllowed,
        zPermissionDenied
    ]),
    easy_match_chat: z.discriminatedUnion('allowed', [
        zPermissionAllowed,
        zPermissionDenied
    ]),
    chat_found_friends: z.discriminatedUnion('allowed', [
        zPermissionAllowed,
        zPermissionDenied
    ])
});

/**
 * UserRank
 *
 * Full rank info for profile owner (UserOut.rank).
 */
export const zUserRank = z.object({
    tier: zRankTier,
    points: z.int().gte(0),
    points_to_next: z.int().gte(0).nullable()
});

/**
 * UserOut
 *
 * User output model (frontend contract).
 */
export const zUserOut = z.object({
    id: z.int(),
    luni_name: z.string().min(1).max(50),
    luni_lastname: z.string().nullable(),
    email: z.string().nullable(),
    tg_user_id: z.string().nullable(),
    gender: z.union([z.literal(1), z.literal(2)]),
    age: z.int().nullable(),
    date_of_birth: z.iso.date().nullable(),
    country_ISO_code: z.string().nullable(),
    app_language: z.enum([
        'ru',
        'en',
        'es'
    ]),
    timezone: z.string().nullable(),
    city: z.string().nullable(),
    interests: z.string().nullable(),
    avatar_urls: zAvatarUrls,
    avatar_id: z.string(),
    avatar_emoji: z.string().nullable(),
    is_invisible: z.boolean(),
    blocked: z.boolean(),
    reason_blocked: z.enum([
        'blocked',
        'temp_blocked',
        'system_blocked',
        'dob_mismatch',
        'parental_consent',
        'bot_blocked',
        'manual',
        'terms_violation',
        'email_not_verified',
        'need_age_verification',
        'trust_gate_pending'
    ]).nullable(),
    subscription: zSubscriptionInfo,
    search_languages: z.array(z.enum([
        'ru',
        'en',
        'es'
    ])),
    luni_id: z.string(),
    character: z.enum([
        'luni',
        'sola',
        'mars'
    ]),
    instagram: z.string().nullable(),
    expertise: z.string().nullable(),
    age_verification: zAgeVerificationOut,
    permissions: zUserPermissions,
    rank: zUserRank
});

/**
 * UserCheckFound
 */
export const zUserCheckFound = z.object({
    exists: z.literal(true),
    user: zUserOut,
    access_token: z.string().min(1),
    refresh_token: z.string().min(1)
});

export const zUserCheckResponse = z.discriminatedUnion('exists', [
    zUserCheckFound,
    zUserCheckMissing
]);

/**
 * UserCheckUpdateFound
 */
export const zUserCheckUpdateFound = z.object({
    exists: z.literal(true),
    user: zUserOut,
    access_token: z.string().min(1),
    refresh_token: z.string().min(1),
    update_screen: z.boolean().optional().default(false)
});

export const zUserCheckUpdateResponse = z.discriminatedUnion('exists', [
    zUserCheckUpdateFound,
    zUserCheckUpdateMissing
]);

/**
 * UserList
 *
 * User list model.
 */
export const zUserList = z.object({
    items: z.array(zUserOut),
    total: z.int()
});

/**
 * UserUpdate
 *
 * User update model.
 */
export const zUserUpdate = z.object({
    id: z.int(),
    luni_name: z.string().min(1).max(50).nullish(),
    luni_lastname: z.string().nullish(),
    email: z.string().nullish(),
    date_of_birth: z.iso.date().nullish(),
    age: z.int().nullish(),
    country_ISO_code: z.string().nullish(),
    app_language: z.enum([
        'ru',
        'en',
        'es'
    ]).nullish(),
    timezone: z.string().nullish(),
    search_languages: z.array(z.enum([
        'ru',
        'en',
        'es'
    ])).nullish(),
    city: z.string().max(50).nullish(),
    interests: z.string().nullish(),
    instagram: z.string().nullish(),
    expertise: z.string().nullish(),
    avatar_id: z.string().nullish(),
    avatar_emoji: z.string().nullish(),
    is_invisible: z.boolean().nullish(),
    blocked: z.literal(true).nullish(),
    reason_blocked: z.enum(['parental_consent', 'need_age_verification']).nullish(),
    character: z.enum([
        'luni',
        'sola',
        'mars'
    ]).nullish()
});

/**
 * ValidatePendingRepliesResponse
 *
 * Response schema for pending replies validation.
 */
export const zValidatePendingRepliesResponse = z.object({
    validated_count: z.int(),
    total_drafts: z.int()
});

/**
 * ValidationError
 */
export const zValidationError = z.object({
    loc: z.array(z.union([z.string(), z.int()])),
    msg: z.string(),
    type: z.string()
});

/**
 * HTTPValidationError
 */
export const zHttpValidationError = z.object({
    detail: z.array(zValidationError).optional()
});

/**
 * ValidationQuestionResponse
 *
 * Response schema for validation question.
 */
export const zValidationQuestionResponse = z.object({
    question: z.string()
});

/**
 * VerifyCodeRequest
 *
 * Request body for POST /auth/email/verify-code.
 */
export const zVerifyCodeRequest = z.object({
    email: z.email(),
    code: z.string(),
    device_id: z.string().min(8).max(255).nullish(),
    tg_user_id: z.string().max(50).nullish()
});

/**
 * VerifyCodeResponse
 *
 * Response for POST /auth/email/verify-code (200).
 */
export const zVerifyCodeResponse = z.object({
    verified: z.boolean()
});

/**
 * VerifyPurchaseErrorResponse
 *
 * Error payload for mobile purchase verification.
 */
export const zVerifyPurchaseErrorResponse = z.object({
    detail: z.string(),
    client_action: z.enum(['finish', 'none'])
});

/**
 * VerifyPurchaseRequest
 *
 * Request payload for mobile purchase verification.
 */
export const zVerifyPurchaseRequest = z.object({
    product_id: z.string(),
    transaction_id: z.string(),
    platform_data: z.record(z.string(), z.string()).optional()
});

/**
 * VerifyPurchaseResponse
 *
 * Response payload for mobile purchase verification.
 */
export const zVerifyPurchaseResponse = z.object({
    success: z.literal(true),
    subscription: zSubscriptionStatusResponse,
    client_action: z.enum(['finish', 'none'])
});

/**
 * VpnKeyResponse
 *
 * Response for GET /user/{user_id}/vpn-key — matches WS vpn notification data.
 */
export const zVpnKeyResponse = z.object({
    vpn_key: z.string().min(1),
    notification_id: z.int()
});

/**
 * Successful Response
 */
export const zUpdateCheckApiV1AppUpdateCheckGetResponse = zAppUpdateCheckOut;

/**
 * Successful Response
 */
export const zRegisterApiV1AuthRegisterPostResponse = zUserCheckResponse;

/**
 * Successful Response
 */
export const zCheckUserExistsApiV1AuthCheckUserPostResponse = zUserCheckUpdateResponse;

/**
 * Successful Response
 */
export const zEmailSendCodeApiV1AuthEmailSendCodePostResponse = z.void();

/**
 * Successful Response
 */
export const zEmailVerifyCodeApiV1AuthEmailVerifyCodePostResponse = zVerifyCodeResponse;

/**
 * Successful Response
 */
export const zEmailLoginSendCodeApiV1AuthEmailLoginSendCodePostResponse = z.void();

/**
 * Successful Response
 */
export const zEmailLoginApiV1AuthEmailLoginPostResponse = zUserCheckResponse;

/**
 * Successful Response
 */
export const zAppleLoginApiV1AuthAppleLoginPostResponse = zUserCheckResponse;

/**
 * Successful Response
 */
export const zGoogleLoginApiV1AuthGoogleLoginPostResponse = zUserCheckResponse;

/**
 * Successful Response
 */
export const zAdminLoginApiV1AuthAdminLoginPostResponse = zAdminLoginResponse;

/**
 * Successful Response
 */
export const zRefreshTokenApiV1AuthTokenRefreshPostResponse = zTokenPairResponse;

/**
 * Successful Response
 */
export const zLogoutApiV1AuthLogoutPostResponse = z.void();

/**
 * Successful Response
 */
export const zRevokeAllSessionsEndpointApiV1AuthSessionsDeleteResponse = zRevokeAllSessionsResponse;

/**
 * Successful Response
 */
export const zListSessionsApiV1AuthSessionsGetResponse = zSessionListResponse;

/**
 * Successful Response
 */
export const zRevokeSessionEndpointApiV1AuthSessionsIdDeleteResponse = z.void();

/**
 * Successful Response
 */
export const zListUsersApiV1AuthUsersGetResponse = zUserList;

/**
 * Successful Response
 */
export const zGetUserInfoByIdApiV1AuthUserUserIdGetResponse = zUserOut;

/**
 * Successful Response
 */
export const zUpdateUserApiV1AuthUpdateUserPostResponse = zUserOut;

/**
 * Successful Response
 */
export const zTrustGateIssueLuniIdApiV1AuthTrustGateIssueLuniIdPostResponse = zUserOut;

/**
 * Successful Response
 */
export const zTrustGateStatusApiV1AuthTrustGateStatusGetResponse = zTrustGateStatusResponse;

/**
 * Successful Response
 */
export const zTrustGateShownApiV1AuthTrustGateShownPostResponse = z.void();

/**
 * Successful Response
 */
export const zTrustGateConfirmApiV1AuthTrustGateConfirmPostResponse = zTrustGateConfirmResponse;

/**
 * Successful Response
 */
export const zUnregisterDeviceTokenApiV1AuthDevicesTokenDeleteResponse = z.void();

/**
 * Successful Response
 */
export const zRegisterDeviceTokenApiV1AuthDevicesTokenPostResponse = z.void();

/**
 * Successful Response
 */
export const zCheckPhotoApiV1AuthCheckPhotoPostResponse = zUserAgeCheckResponseV2;

/**
 * Successful Response
 */
export const zCheckPhotoQualityApiV1AuthCheckPhotoQualityPostResponse = zPhotoQualityResponse;

/**
 * Successful Response
 */
export const zBlockUserEndpointApiV1AuthUserUserIdBlockPostResponse = zBlockUserResponse;

/**
 * Successful Response
 */
export const zUnblockUserEndpointApiV1AuthUserUserIdUnblockPostResponse = zBlockUserResponse;

/**
 * Successful Response
 */
export const zDeleteUserEndpointApiV1AuthUserDeleteUserIdDeleteResponse = z.void();

/**
 * Successful Response
 */
export const zGetInviteContextApiV1AuthInviteContextGetResponse = zInviteContextResponse;

/**
 * Successful Response
 */
export const zCreateAdminApiV1AuthAdminCreatePostResponse = zAdminOut;

/**
 * Successful Response
 */
export const zGetAdminApiV1AuthAdminAdminIdGetResponse = zAdminOut;

/**
 * Successful Response
 */
export const zUpdateAdminApiV1AuthAdminAdminIdPutResponse = zAdminOut;

/**
 * Successful Response
 */
export const zGetUserCardsApiV1AuthUserUserIdCardsGetResponse = zCardsResponse;

/**
 * Successful Response
 */
export const zCreateCardApiV1AuthUserUserIdCardsPostResponse = zCardOut;

/**
 * Successful Response
 */
export const zDeleteCardApiV1AuthUserUserIdCardsCardIdDeleteResponse = z.void();

/**
 * Successful Response
 */
export const zUpdateCardApiV1AuthUserUserIdCardsCardIdPutResponse = zCardOut;

/**
 * Successful Response
 */
export const zLikeCardApiV1AuthCardsCardIdLikePostResponse = z.void();

/**
 * Successful Response
 */
export const zStarCardApiV1AuthCardsCardIdStarPostResponse = z.void();

/**
 * Successful Response
 */
export const zHideCardApiV1AuthCardsCardIdHidePostResponse = z.void();

/**
 * Successful Response
 */
export const zReportCardApiV1AuthCardsCardIdReportPostResponse = z.void();

/**
 * Successful Response
 */
export const zGetAvatarsApiV1AuthAvatarsGetResponse = zAvatarsListResponse;

/**
 * Successful Response
 */
export const zGetUserVpnKeyApiV1AuthUserUserIdVpnKeyGetResponse = zVpnKeyResponse;

/**
 * Successful Response
 */
export const zGetFeedApiV1AuthFeedGetResponse = zFeedListResponse;

/**
 * Successful Response
 */
export const zGetFeedStatusApiV1AuthFeedStatusGetResponse = zFeedStatusResponse;

/**
 * Successful Response
 */
export const zGetQotdCurrentApiV1AuthFeedQotdCurrentGetResponse = zQotdCurrentResponse;

/**
 * Successful Response
 */
export const zPostQotdAnswerApiV1AuthFeedQotdAnswerPostResponse = z.void();

/**
 * Successful Response
 */
export const zGetUserPersonalityApiV1AuthUserUserIdPersonalityGetResponse = zPersonalityProfileResponse;

/**
 * Successful Response
 */
export const zGetFriendPersonalityApiV1AuthFriendsFriendIdPersonalityGetResponse = zPersonalityProfileResponse;

/**
 * Successful Response
 */
export const zPatchUserPersonalityVisibilityApiV1AuthUserUserIdPersonalityVisibilityPatchResponse = z.void();

/**
 * Successful Response
 */
export const zCreatePersonalityImageUploadUrlApiV1AuthUserUserIdPersonalityImagesPresignPostResponse = zPersonalityImageUploadResponse;

/**
 * Successful Response
 */
export const zGetUserEiApiV1AuthUserUserIdEiGetResponse = zEiProfileResponse;

/**
 * Successful Response
 */
export const zPatchEiVisibilityApiV1AuthUserUserIdEiVisibilityPatchResponse = z.void();

/**
 * Successful Response
 */
export const zStartEiTestApiV1AuthEiTestStartPostResponse = zEiSessionResponse;

/**
 * Successful Response
 */
export const zGetEiTestApiV1AuthEiTestGetResponse = zEiSessionResponse;

/**
 * Successful Response
 */
export const zSaveEiAnswersApiV1AuthEiTestAnswersPutResponse = zEiSaveAnswersResponse;

/**
 * Successful Response
 */
export const zCompleteEiTestApiV1AuthEiTestCompletePostResponse = zEiResultsResponse;

/**
 * Successful Response
 */
export const zGetEiResultsApiV1AuthEiResultsGetResponse = zEiResultsResponse;

/**
 * Successful Response
 */
export const zGetEiLeaderboardApiV1AuthEiLeaderboardGetResponse = zEiLeaderboardResponse;

/**
 * Successful Response
 */
export const zLuniMessageExchangeApiV1LuniLuniMessageExchangePostResponse = zLuniMessageResponse;

/**
 * Successful Response
 */
export const zAnalyzeAccentuationApiV1LuniAnalyzeAccentuationUserIdPostResponse = zAccentuationAnalysisResponse;

/**
 * Successful Response
 */
export const zGetValidationQuestionApiV1LuniValidationQuestionUserIdGetResponse = zValidationQuestionResponse;

/**
 * Successful Response
 */
export const zValidatePendingRepliesApiV1LuniValidatePendingRepliesPostResponse = zValidatePendingRepliesResponse;

/**
 * Successful Response
 */
export const zGetLastMessageApiV1LuniLuniGetLastMessageGetResponse = zLuniLastMessageResponse;

/**
 * Successful Response
 */
export const zCharacterSendMessageApiV1CharacterCharacterSendMessagePostResponse = zCharacterSendMessageResponse;

/**
 * Response
 *
 * Successful Response
 */
export const zCharacterStreamApiV1CharacterCharacterStreamPostResponse = z.string();

/**
 * Successful Response
 */
export const zCharacterLastMessageApiV1CharacterCharacterLastMessageGetResponse = zCharacterLastMessageResponse;

/**
 * Successful Response
 */
export const zSendSupportMessageApiV1SupportSendPostResponse = zSupportMessageResponse;

/**
 * Successful Response
 */
export const zGetUserThoughtsApiV1UsersGetResponse = zThoughtsListResponse;

/**
 * Successful Response
 */
export const zCreateThoughtApiV1UsersPostResponse = zThoughtResponse;

/**
 * Successful Response
 */
export const zDeleteThoughtByUserIdApiV1UsersUserIdThoughtsThoughtIdDeleteResponse = z.void();

/**
 * Successful Response
 */
export const zGetUserThoughtByUserAndIdApiV1UsersUserIdThoughtsThoughtIdGetResponse = zThoughtResponse;

/**
 * Successful Response
 */
export const zUpdateThoughtByUserIdApiV1UsersUserIdThoughtsThoughtIdPutResponse = zThoughtResponse;

/**
 * Response Get User Thoughts By Id Api V1 Users  User Id  Thoughts Get
 *
 * Successful Response
 */
export const zGetUserThoughtsByIdApiV1UsersUserIdThoughtsGetResponse = z.array(zThoughtResponse);

/**
 * Successful Response
 */
export const zCreateThoughtByUserIdApiV1UsersUserIdThoughtsPostResponse = zThoughtResponse;

/**
 * Successful Response
 */
export const zDeleteThoughtApiV1UsersThoughtIdDeleteResponse = z.void();

/**
 * Successful Response
 */
export const zGetThoughtApiV1UsersThoughtIdGetResponse = zThoughtResponse;

/**
 * Successful Response
 */
export const zUpdateThoughtApiV1UsersThoughtIdPutResponse = zThoughtResponse;

/**
 * Successful Response
 */
export const zGetThoughtsByDateApiV1UsersDateThoughtsDateGetResponse = zThoughtsByDateResponse;

/**
 * Successful Response
 */
export const zMarkThoughtAsReadApiV1UsersThoughtIdReadPatchResponse = zThoughtResponse;

/**
 * Successful Response
 */
export const zMarkThoughtAsReadAliasApiV1UsersUserIdThoughtsThoughtIdMarkReadPostResponse = zThoughtResponse;

/**
 * Successful Response
 */
export const zGetAiGeneratedThoughtsApiV1UsersAiGeneratedGetResponse = zAiThoughtsResponse;

/**
 * Successful Response
 */
export const zGetDotPlotMetaApiV1AnalyticsAdminDotPlotMetaGetResponse = zDotPlotMeta;

/**
 * Successful Response
 */
export const zGetDotPlotApiV1AnalyticsAdminDotPlotGetResponse = zDotPlotChunkResponse;

/**
 * Response Rebuild Dot Plot Api V1 Analytics Admin Dot Plot Rebuild Post
 *
 * Successful Response
 */
export const zRebuildDotPlotApiV1AnalyticsAdminDotPlotRebuildPostResponse = z.record(z.string(), z.int());

/**
 * Successful Response
 */
export const zGetPushTrackingMetaApiV1AnalyticsAdminPushTrackingMetaGetResponse = zPushTrackingMeta;

/**
 * Successful Response
 */
export const zGetPushTrackingApiV1AnalyticsAdminPushTrackingGetResponse = zPushTrackingMatrixResponse;

/**
 * Response Rebuild Push Tracking Api V1 Analytics Admin Push Tracking Rebuild Post
 *
 * Successful Response
 */
export const zRebuildPushTrackingApiV1AnalyticsAdminPushTrackingRebuildPostResponse = z.record(z.string(), z.int());

/**
 * Successful Response
 */
export const zRecordAppPageVisitApiV1AnalyticsPageVisitsAppPostResponse = z.void();

/**
 * Successful Response
 */
export const zRecordDiaryPageVisitApiV1AnalyticsPageVisitsDiaryPostResponse = z.void();

/**
 * Successful Response
 */
export const zRecordFeedPageVisitApiV1AnalyticsPageVisitsFeedPostResponse = z.void();

/**
 * Successful Response
 */
export const zGetUserAnalyticsApiV1AnalyticsUsersGetResponse = zUserAnalytics;

/**
 * Successful Response
 */
export const zGetFriendshipAnalyticsApiV1AnalyticsFriendshipsGetResponse = zFriendshipAnalytics;

/**
 * Successful Response
 */
export const zGetLuniAnalyticsApiV1AnalyticsLuniGetResponse = zLuniAnalytics;

/**
 * Successful Response
 */
export const zGetSystemMetricsApiV1AnalyticsSystemGetResponse = zSystemMetrics;

/**
 * Successful Response
 */
export const zGetPendingRequestsAnalyticsApiV1AnalyticsPendingRequestsGetResponse = zPendingRequestsAnalytics;

/**
 * Successful Response
 */
export const zGetRankStatsApiV1RanksStatsGetResponse = zRankStatsResponse;

/**
 * Successful Response
 */
export const zPostAndroidInstallReferrerApiV1AttributionAndroidInstallReferrerPostResponse = z.void();

/**
 * Successful Response
 */
export const zSaveOnboardingIntentionApiV1OnboardingIntentionPostResponse = z.void();

/**
 * Successful Response
 */
export const zSaveOnboardingVpnApiV1OnboardingVpnPostResponse = z.void();

/**
 * Successful Response
 */
export const zSaveOnboardingHeardAboutApiV1OnboardingHeardAboutPostResponse = z.void();

/**
 * Successful Response
 */
export const zSaveOnboardingInstagramApiV1OnboardingInstagramPostResponse = z.void();

/**
 * Successful Response
 */
export const zTranslateApiV1TranslatePostResponse = zTranslateResponse;

/**
 * Successful Response
 */
export const zRegisterTelegramUserApiV1TelegramRegisterPostResponse = zTelegramUserOut;

/**
 * Successful Response
 */
export const zGetTelegramUserEndpointApiV1TelegramTelegramIdGetResponse = zTelegramUserOut;

/**
 * Successful Response
 */
export const zUpdateTelegramUserEndpointApiV1TelegramTelegramIdPutResponse = zTelegramUserOut;

/**
 * Successful Response
 */
export const zAppleVerifyPurchaseApiV1PaymentsAppleVerifyPostResponse = zVerifyPurchaseResponse;

/**
 * Successful Response
 */
export const zSubscriptionStatusApiV1SubscriptionStatusGetResponse = zSubscriptionStatusResponse;

/**
 * Successful Response
 */
export const zAppleAppAccountTokenApiV1SubscriptionAppleAppAccountTokenGetResponse = zAppleAppAccountTokenResponse;

/**
 * Successful Response
 */
export const zSubscriptionCancelApiV1SubscriptionCancelPostResponse = z.void();

/**
 * Successful Response
 */
export const zVerifyPurchaseApiV1SubscriptionVerifyPurchasePostResponse = zVerifyPurchaseResponse;
